// Angular
import { inject } from '@angular/core';

// NGRX
import { Observable, of } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { map, filter, take, switchMap, catchError } from 'rxjs/operators';

// Miscellaneous
import * as olci from '../actions/olci';
import * as fromRoot from '../reducers';
import { constants } from '../constants/constants';
import { ObjectManagerUtils } from '../utils/object-manager.utils';
import { GuestService } from '../modules/core/services/guest.service';
import { MicroServicesService } from '../modules/core/services/micro-services.service';
import { CanActivateFn } from '@angular/router'; 

export const arrivalTimeInvAppointmentGuard: CanActivateFn = (): Observable<any> => {
  const store: Store<fromRoot.AppState> = inject(Store<fromRoot.AppState>);
	const guestService: GuestService = inject(GuestService);
  const microService: MicroServicesService = inject(MicroServicesService);
  const objectManagerUtils: ObjectManagerUtils = inject(ObjectManagerUtils);
  
  return store.pipe(
    select(fromRoot.getSessionState),
    filter(resData => resData.reservationRetrievalResp !== null || !!resData.error),
    take(1),
    switchMap(resData => {
      if (resData.error) {
        store.dispatch(new olci.NavigateToError());
        return of(false);
      }
      return dispatchSuccessProcess(store, guestService, microService, objectManagerUtils);
    })
  );
}

const dispatchSuccessProcess = (store: Store<fromRoot.AppState>, guestService: GuestService, microService: MicroServicesService, objectManagerUtils: ObjectManagerUtils) => {
    store.dispatch(new olci.SessionTimestampRenewedAction());
    const gaGuestId = guestService.loggedInGuestPaxId;
    const loggedInGuest = retrieveLoggedInGuest(gaGuestId, guestService, objectManagerUtils);

    let guestReservationIds: any = [];
    const getArrivalApptObjectModel = {
      guestReservationIds: [{ bookingId: '', guestId: '' }]
    };

    store
      .pipe(
        select(fromRoot.getEntitySGuests),
        take(1)
      )
      .subscribe(guests => {
        if (guests !== null) {
          guestReservationIds = guests.map((guest: any) => ({
            bookingId: guest.bookingId,
            guestId: guest.guestId
          }));
        }
      });

    getArrivalApptObjectModel.guestReservationIds = guestReservationIds;

    return microService
      .getArrivalAppointmentsAndInventory(getArrivalApptObjectModel)
      .pipe(map((arrivalTimes: any) => {
        if (arrivalTimes.status !== constants.HTTP_STATUS_CODE.SUCCESS || !arrivalTimes.payload.arrivalAppointmentInventory.length) {
          store.dispatch(new olci.NavigateToError());
          return false;
        }
        return dispatchSuccessArrivalTimes(arrivalTimes, loggedInGuest, store);
      })
        , catchError((error: any) => {
          if (error.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
            store.dispatch(new olci.NavigateToError());
          }
          return of(false);
        }));
  }

  const dispatchSuccessArrivalTimes = (arrivalTimes: any, loggedInGuest: any, store: Store<fromRoot.AppState>) => {
    store
      .pipe(
        select(fromRoot.getEntitySelectedOther),
        take(1)
      )
      .subscribe(res => {
        if (res) {
          arrivalTimes.firstAdultId = parseInt(res.guestId, 10);
        } else {
          arrivalTimes.firstAdultId = parseInt(loggedInGuest, 10);
        }
        store.dispatch(new olci.LoadArrivalAppointmentAndInventorySuccessAction(arrivalTimes));
      });

    return !!arrivalTimes;
  }

  const retrieveLoggedInGuest = (gaGuestId: any, guestService: any, objectManagerUtils: any) => {
    const firstAdult = guestService.getFirstAdultInTheReservation();
    return gaGuestId
      ? gaGuestId
      : objectManagerUtils.ternaryValidation(
        firstAdult,
        firstAdult?.guestId,
        constants.DEFAULT_GUEST_ID_FOR_TRACKING.ID
      );
  }
